/**
 * The Partner with us form, as data — `Just Treated Partner With Us` frame.
 *
 * Everything the form asks maps onto the platform's own provider onboarding
 * (the journey the partner portal runs): the frame's business types onto the
 * platform's organisation types and sectors, the regulator number onto the
 * primary branch, the plan onto the fulfilment methods the onboarding already
 * records. Nothing here is a second onboarding model.
 *
 * The frame draws nine business types and a regulator rule for each
 * (`REG` in its logic); its two-step form never shows the number field, which
 * the v1 frame did. It is drawn here on step 1, labelled for the chosen type,
 * because the plan asks for it and the platform's onboarding needs it.
 */

export type BusinessTypeId =
  'community' | 'online' | 'clinic' | 'dental' | 'optician' | 'aesthetics' | 'physio' | 'vet' | 'health';

export interface RegulatorRule {
  readonly label: string;
  readonly placeholder: string;
  readonly help: string;
  /** The register the number belongs to, as stored on the application. */
  readonly body: string;
  readonly optional: boolean;
  readonly pattern: RegExp;
  readonly error: string;
}

export interface BusinessType {
  readonly id: BusinessTypeId;
  readonly label: string;
  /** The platform's organisation type (`ORGANISATION_TYPES`). */
  readonly organisationType: string;
  /** The platform's sector (`SECTOR_CATALOGUE`) and, where the frame's type names one, its service. */
  readonly sector: string;
  readonly services: readonly string[];
  readonly regulator: RegulatorRule;
}

const GPHC: Omit<RegulatorRule, 'placeholder' | 'help'> = {
  label: 'GPhC premises number',
  body: 'GPHC',
  optional: false,
  pattern: /^\d{7}$/,
  error: 'Enter the 7-digit GPhC premises number',
};
const CQC: Omit<RegulatorRule, 'help'> = {
  label: 'CQC provider ID',
  placeholder: 'e.g. 1-123456789',
  body: 'CQC',
  optional: false,
  pattern: /^1-\d{6,11}$/,
  error: 'Enter a CQC provider ID, starting 1-',
};

/** The frame's nine types, in its order, with its own register wording. */
export const BUSINESS_TYPES: readonly BusinessType[] = [
  {
    id: 'community',
    label: 'Community pharmacy',
    organisationType: 'pharmacy',
    sector: 'PHARMACY_MEDICINES',
    services: ['COMMUNITY_PHARMACY'],
    regulator: { ...GPHC, placeholder: 'e.g. 1012345', help: '7 digits. Find it on the GPhC register.' },
  },
  {
    id: 'online',
    label: 'Online / distance-selling pharmacy',
    organisationType: 'pharmacy',
    sector: 'PHARMACY_MEDICINES',
    services: ['ONLINE_PHARMACY'],
    regulator: {
      ...GPHC,
      placeholder: 'e.g. 9012345',
      help: 'Distance-selling pharmacies must also show the EU common logo.',
    },
  },
  {
    id: 'clinic',
    label: 'Clinic or private GP',
    organisationType: 'clinic',
    sector: 'MEDICAL_CLINICAL',
    services: [],
    regulator: { ...CQC, help: 'Starts with 1-. Find it on the CQC website.' },
  },
  {
    id: 'dental',
    label: 'Dental practice',
    organisationType: 'dental_practice',
    sector: 'DENTAL',
    services: [],
    regulator: { ...CQC, help: 'Starts with 1-. Find it on the CQC website.' },
  },
  {
    id: 'optician',
    label: 'Opticians',
    organisationType: 'optician',
    sector: 'OPTICAL_HEARING',
    services: [],
    regulator: {
      label: 'GOC business registration',
      placeholder: 'e.g. B12345',
      help: 'From the General Optical Council register.',
      body: 'GOC',
      optional: false,
      pattern: /^[A-Za-z]?\d{4,6}$/,
      error: 'Enter the GOC business registration, like B12345',
    },
  },
  {
    id: 'aesthetics',
    label: 'Beauty & aesthetics clinic',
    organisationType: 'aesthetics_clinic',
    sector: 'AESTHETICS_BEAUTY_PERSONAL_CARE',
    services: [],
    regulator: {
      label: 'CQC provider ID or local licence',
      placeholder: 'e.g. 1-123456789',
      help: 'Treatments involving prescription medicines need a CQC-registered prescriber.',
      body: 'CQC_OR_LOCAL_LICENCE',
      optional: false,
      pattern: /^.{4,}$/,
      error: 'Enter your CQC provider ID or local licence number',
    },
  },
  {
    id: 'physio',
    label: 'Physiotherapy & wellbeing',
    organisationType: 'physiotherapy_provider',
    sector: 'ALLIED_HEALTH_THERAPY',
    services: [],
    regulator: {
      label: 'HCPC number of lead clinician',
      placeholder: 'e.g. PH12345',
      help: 'From the HCPC register.',
      body: 'HCPC',
      optional: false,
      pattern: /^[A-Za-z]{2}\d{4,6}$/,
      error: 'Enter the HCPC number, like PH12345',
    },
  },
  {
    id: 'vet',
    label: 'Vet pharmacy',
    organisationType: 'veterinary_clinic',
    sector: 'VETERINARY_ANIMAL_CARE',
    services: [],
    regulator: {
      label: 'RCVS premises number',
      placeholder: 'e.g. 123456',
      help: 'From the RCVS register of practice premises.',
      body: 'RCVS',
      optional: false,
      pattern: /^\d{4,8}$/,
      error: 'Enter the RCVS premises number (4 to 8 digits)',
    },
  },
  {
    id: 'health',
    label: 'Health & nutrition store',
    organisationType: 'supplier',
    sector: 'HEALTH_RETAIL_TECHNOLOGY',
    services: ['HEALTH_RETAIL'],
    regulator: {
      label: 'Companies House number (optional)',
      placeholder: 'e.g. 12345678',
      help: 'Helps us verify your business faster.',
      body: 'COMPANIES_HOUSE',
      optional: true,
      pattern: /^(?:[A-Za-z]{2}\d{6}|\d{8})$/,
      error: 'Enter an 8-character Companies House number, like 12345678',
    },
  },
];

/** Before a type is chosen, as the frame words it. */
export const NO_TYPE_REGULATOR = {
  label: 'Registration number',
  placeholder: 'Choose a business type first',
  help: "We'll check it against the right regulator.",
} as const;

export function businessType(id: string | undefined): BusinessType | undefined {
  return BUSINESS_TYPES.find((type) => type.id === id);
}

export const SITE_OPTIONS = [
  { id: '1', label: '1 site' },
  { id: '2-5', label: '2 to 5 sites' },
  { id: '6-20', label: '6 to 20 sites' },
  { id: '21+', label: '21 or more sites' },
] as const;

export const ROLE_OPTIONS = [
  { id: 'owner', label: 'Owner or director' },
  { id: 'si', label: 'Superintendent pharmacist' },
  { id: 'rm', label: 'Registered or clinic manager' },
  { id: 'other', label: 'Other' },
] as const;

/**
 * The frame's two plans, as the fulfilment the onboarding already records
 * (`PRODUCTS_FULFILMENT.fulfilmentMethods`): no new field, no price.
 */
export type PlanId = 'own' | 'jt';
export const PLAN_FULFILMENT: Readonly<Record<PlanId, string>> = {
  own: 'OWN_DELIVERY',
  jt: 'PLATFORM_COURIER',
};
export const PLAN_LABEL: Readonly<Record<PlanId, string>> = {
  own: 'You deliver',
  jt: 'Just Treated delivers',
};

export interface PartnerForm {
  readonly biz: string;
  readonly type: string;
  readonly addr: string;
  readonly pc: string;
  readonly sites: string;
  readonly reg: string;
  readonly first: string;
  readonly last: string;
  readonly phone: string;
  readonly email: string;
  readonly role: string;
}

export const EMPTY_FORM: PartnerForm = {
  biz: '',
  type: '',
  addr: '',
  pc: '',
  sites: '',
  reg: '',
  first: '',
  last: '',
  phone: '',
  email: '',
  role: '',
};

export type FormErrors = Partial<Record<keyof PartnerForm, string>>;

export const STEP_ONE_FIELDS = ['biz', 'type', 'addr', 'pc', 'sites', 'reg'] as const;
export const STEP_TWO_FIELDS = ['first', 'last', 'phone', 'email', 'role'] as const;

const POSTCODE = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/;
/** The platform's own shape for a business phone (`section-schema.ts` PHONE), so the saved section is complete. */
const PHONE = /^(\+44\s?|0)[\d\s]{9,13}$/;
/** The platform's own shape for a UK mobile (REPRESENTATIVE.mobile). */
const MOBILE = /^(\+44\s?7\d{3}|07\d{3})\s?\d{3}\s?\d{3}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateStepOne(form: PartnerForm): FormErrors {
  const errors: FormErrors = {};
  if (form.biz.trim().length < 2) errors.biz = 'Enter your business name';
  const type = businessType(form.type);
  if (type === undefined) errors.type = 'Choose a business type';
  if (form.addr.trim() === '') errors.addr = 'Enter the street address';
  if (!POSTCODE.test(form.pc.trim())) errors.pc = 'Enter a valid UK postcode';
  if (form.sites === '') errors.sites = 'Choose how many sites';
  if (type !== undefined) {
    const reg = form.reg.trim();
    if (reg === '') {
      if (!type.regulator.optional) errors.reg = `Enter your ${type.regulator.label}`;
    } else if (!type.regulator.pattern.test(reg)) {
      errors.reg = type.regulator.error;
    }
  }
  return errors;
}

export function validateStepTwo(form: PartnerForm): FormErrors {
  const errors: FormErrors = {};
  if (form.first.trim() === '') errors.first = 'Enter a first name';
  if (form.last.trim() === '') errors.last = 'Enter a last name';
  if (!PHONE.test(form.phone.trim())) errors.phone = 'Enter a UK phone number';
  if (!EMAIL.test(form.email.trim())) errors.email = 'Enter a valid email address';
  if (form.role === '') errors.role = 'Choose your role';
  return errors;
}

/** A UK postcode in its usual written form: upper case, one space before the last three. */
export function formatPostcode(raw: string): string {
  const compact = raw.replace(/\s+/g, '').toUpperCase();
  return compact.length > 3 ? `${compact.slice(0, -3)} ${compact.slice(-3)}` : compact;
}

type AnswerValue = string | number | boolean | readonly string[];

export interface SectionPrefill {
  readonly key: string;
  readonly answers: Readonly<Record<string, AnswerValue>>;
  /**
   * Marked complete only when the answers satisfy the platform's own rule for
   * that section; everything else is saved in progress for the applicant to
   * confirm in the partner portal.
   */
  readonly complete: boolean;
}

export interface ApplicationPrefill {
  readonly start: {
    readonly organisationType: string;
    readonly legalName: string;
    readonly tradingName: string;
  };
  readonly sections: readonly SectionPrefill[];
  readonly branch: { readonly name: string; readonly regulatoryReference?: string; readonly primary: true };
}

/**
 * Turns the form into the platform's application: the start body, the
 * sections it prefills and the primary branch.
 *
 * The answer keys are the partner portal's own (`journey/fields.ts`), so the
 * applicant finds them filled in when they continue there. The few the portal
 * does not ask (sites, the register a number belongs to) are extra keys in the
 * same opaque section record, which the platform stores as given.
 */
export function toApplication(form: PartnerForm, plan: PlanId | undefined): ApplicationPrefill {
  const type = businessType(form.type);
  if (type === undefined) throw new Error('A business type is required.');
  const biz = form.biz.trim();
  const postcode = formatPostcode(form.pc);
  const phone = form.phone.trim();
  const email = form.email.trim();
  const reg = form.reg.trim();
  const role = ROLE_OPTIONS.find((option) => option.id === form.role)?.label ?? form.role;

  const sections: SectionPrefill[] = [
    {
      key: 'BUSINESS_DETAILS',
      answers: {
        tradingName: biz,
        principalTradingAddress: `${form.addr.trim()}, ${postcode}`,
        principalTradingPostcode: postcode,
        numberOfSites: form.sites,
      },
      complete: true,
    },
    { key: 'BUSINESS_CONTACT', answers: { email, phone }, complete: true },
    {
      key: 'REPRESENTATIVE',
      answers: {
        legalName: `${form.first.trim()} ${form.last.trim()}`,
        roleInBusiness: role,
        businessEmail: email,
        ...(MOBILE.test(phone) ? { mobile: phone } : {}),
      },
      // The portal still asks for the authority-to-act declaration and how to
      // contact them; those are the applicant's to give, not this form's.
      complete: false,
    },
    {
      key: 'SECTOR_ACTIVITIES',
      answers: {
        primarySector: type.sector,
        ...(type.services.length > 0 ? { services: type.services } : {}),
      },
      complete: false,
    },
  ];
  if (reg !== '') {
    sections.push({
      key: 'REGULATORY_INFORMATION',
      answers: { registrationBody: type.regulator.body, registrationNumber: reg },
      complete: false,
    });
  }
  if (plan !== undefined) {
    sections.push({
      key: 'PRODUCTS_FULFILMENT',
      answers: { fulfilmentMethods: [PLAN_FULFILMENT[plan]] },
      complete: false,
    });
  }

  return {
    start: { organisationType: type.organisationType, legalName: biz, tradingName: biz },
    sections,
    branch: { name: biz, ...(reg !== '' ? { regulatoryReference: reg } : {}), primary: true },
  };
}

/** The application's status in plain words — the partner portal's own labels. */
export const APPLICATION_STATUS_LABEL: Readonly<Record<string, string>> = {
  DRAFT: 'Not started',
  IN_PROGRESS: 'In progress',
  READY_TO_SUBMIT: 'Ready to submit',
  SUBMITTED: 'Under review',
  UNDER_REVIEW: 'Under review',
  MORE_INFORMATION_REQUIRED: 'Action required',
  RESUBMITTED: 'Under review',
  APPROVED: 'Approved',
  APPROVED_WITH_RESTRICTIONS: 'Approved with conditions',
  REJECTED: 'Not approved',
  WITHDRAWN: 'Withdrawn',
  EXPIRED: 'Expired',
  ACTIVATED: 'Active',
};

/** Statuses where the applicant still has to finish and submit in the portal. */
export const APPLICANT_TO_FINISH: ReadonlySet<string> = new Set(['DRAFT', 'IN_PROGRESS', 'READY_TO_SUBMIT']);
